import type { Category, Phrase } from '../types';

/**
 * How much the phrase bank can show, in one place (ADR-040, ADR-044, `AUT-08`).
 *
 * The Sätze grid is twelve slots — `Saetze.tsx` renders `SAETZE_COLUMNS` columns of `1fr` rows
 * inside a fixed height. Nothing enforced any number at first, so an extra phrase in a category
 * made an unbudgeted row inside that height: every tile below `--min-target`, or a grid that
 * overflows onto the bar beneath it, and no word about it on screen. **A phrase the person cannot
 * see is a sentence they cannot say.**
 *
 * The numbers live here rather than in care mode so the grid and the rule that guards it cannot
 * drift apart — the surface renders from these and the caregiver panels refuse from these, the
 * way `LAYOUT_COLUMNS` is the single source for the width of the board.
 *
 * ── Why twelve is 4 × 3 and not 3 × 4 (ADR-044) ──
 *
 * A tester reported eleven phrases as still legible, having seen them because the import checks no
 * capacity at all (C85) and the three-column grid made a fourth row. **The count is theirs and the
 * shape is arithmetic**: with the audio banner up, a fourth row of `--min-target` tiles does not fit
 * in what is left of the surface, and the grid overflows rather than shrinking. `GEO-23` owns that
 * calculation and asserts it against the device's height rather than the container's.
 */

export const SAETZE_COLUMNS = 4;
export const SAETZE_ROWS = 3;
export const SAETZE_SLOTS = SAETZE_COLUMNS * SAETZE_ROWS;

/** One slot goes to the back tile, which is the whole of the difference between the two steps. */
export const PHRASES_PER_CATEGORY = SAETZE_SLOTS - 1;

/** The category step has no back tile: every slot is a category. */
export const CATEGORIES_PER_SCREEN = SAETZE_SLOTS;

/**
 * Notfall is excluded from Sätze because it has a surface of its own one action away (`SUR-10`),
 * so it costs no slot here.
 */
export const HIDDEN_IN_SAETZE = 'cat-notfall';

/** The categories the phrase screen shows, and therefore the ones that take up its slots. */
export function shownInSaetze(categories: readonly Category[]): Category[] {
  return categories.filter((category) => category.id !== HIDDEN_IN_SAETZE);
}

/**
 * `<` rather than `!==`: a category already over the line — from seed data, or from a release
 * that predates this rule — has no room either, and an equality test would hand out a slot that
 * does not exist.
 */
export function hasRoomForPhrase(phrasesInCategory: number): boolean {
  return phrasesInCategory < PHRASES_PER_CATEGORY;
}

export function hasRoomForCategory(categoriesShown: number): boolean {
  return categoriesShown < CATEGORIES_PER_SCREEN;
}

/**
 * Whether a phrase can sit in that category — as a new row, or as one arriving from elsewhere.
 *
 * `movingId` is the phrase being edited. Without it this asks "is there a free slot"; with it, a
 * phrase already in that category is not asking for a slot, so rewording something inside a full
 * category is allowed and only a move from outside is refused (`AUT-04`).
 */
export function canPlacePhrase(
  phrases: readonly Phrase[],
  categoryId: string,
  movingId?: string,
): boolean {
  const occupants = phrases.filter(
    (phrase) => phrase.categoryId === categoryId && phrase.id !== movingId,
  );
  return hasRoomForPhrase(occupants.length);
}

/**
 * Where a new phrase can go and still be reachable.
 *
 * The mining path needs this rather than a fixed category: `cat-gespraech` used to ship at the cap, so
 * `Übernehmen` had nowhere to put a tile the person could actually see. Since ADR-044 raised the cap it
 * ships with three slots free — which makes the preference in `keepDestination` actually win, and leaves
 * this function doing the job it was written for on any board a caregiver has since filled.
 */
export function categoriesWithRoom(
  categories: readonly Category[],
  phrases: readonly Phrase[],
): Category[] {
  return shownInSaetze(categories).filter((category) =>
    hasRoomForPhrase(phrases.filter((phrase) => phrase.categoryId === category.id).length),
  );
}
